import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface AIPlayerRAGChunk {
  text: string
  filename: string
  documentId: number | null
  score: number
  chunkIndex: number
}

export interface AIPlayerTestMessage {
  id: number | string
  message: string
  streaming: boolean
  user: string
  ragChunks?: AIPlayerRAGChunk[]
  llm_provider?: string
  llm_model_name?: string
  created_at?: string
}

export interface AIPlayerTestMessageData {
  id: number
  message: string
  user: string
  created_at: string
  llm_provider?: string
  llm_model_name?: string
}

export interface ExistingAIPlayerTestChat {
  id: number
  name: string
  created_at: string
  temperature: number
  max_tokens: number
  history_limit: number
  llm_model: string
  message_count: number
  messages: AIPlayerTestMessageData[]
}

export const AI_PLAYER_TEST_CHAT_ID = -1

const BASE_RIGHT = 16
const BASE_BOTTOM = 16
const BOX_WIDTH = 448
const BOX_SPACING = 16

export interface AIPlayerOpenChatBox {
  id: number
  chatData: ExistingAIPlayerTestChat | null
  isReadOnly: boolean
  isTestChat?: boolean
  position: { right: number; bottom: number }
  dimensions: { width: number; height: number }
}

interface AIPlayerTestChatState {
  messages: AIPlayerTestMessage[]
  isConnected: boolean
  error: string | null
  isGenerating: boolean
  isTestChatOpen: boolean
  existingTestChats: ExistingAIPlayerTestChat[]
  openChatBoxes: AIPlayerOpenChatBox[]
}

const initialState: AIPlayerTestChatState = {
  messages: [],
  isConnected: false,
  error: null,
  isGenerating: false,
  isTestChatOpen: false,
  existingTestChats: [],
  openChatBoxes: [],
}

const aiPlayerTestChatSlice = createSlice({
  name: 'aiPlayerTestChatMessages',
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload
    },
    addMessage: (state, action: PayloadAction<AIPlayerTestMessage>) => {
      const mergeMetadata = (target: AIPlayerTestMessage) => {
        if (action.payload.created_at !== undefined) {
          target.created_at = action.payload.created_at
        }
        if (action.payload.llm_provider !== undefined) {
          target.llm_provider = action.payload.llm_provider
        }
        if (action.payload.llm_model_name !== undefined) {
          target.llm_model_name = action.payload.llm_model_name
        }
      }

      if (action.payload.streaming) {
        if (state.messages.length > 0) {
          const lastMessage = state.messages[state.messages.length - 1]
          if (lastMessage.streaming) {
            if (action.payload.message) {
              lastMessage.message += action.payload.message
            }
            if (action.payload.ragChunks !== undefined) {
              lastMessage.ragChunks = action.payload.ragChunks
            }
            mergeMetadata(lastMessage)
            return
          }
        }
        state.messages.push(action.payload)
      } else if (!action.payload.streaming) {
        if (
          state.messages.length > 0 &&
          state.messages[state.messages.length - 1].streaming
        ) {
          const lastMessage = state.messages[state.messages.length - 1]
          state.messages[state.messages.length - 1] = {
            ...lastMessage,
            ...action.payload,
            message: action.payload.message || lastMessage.message,
            streaming: false,
            ragChunks: action.payload.ragChunks ?? lastMessage.ragChunks,
          }
        } else {
          state.messages.push(action.payload)
        }
      } else {
        state.messages.push(action.payload)
      }
    },
    updateMessage: (state, action: PayloadAction<AIPlayerTestMessage>) => {
      const messageIndex = state.messages.findIndex(
        (msg) => String(msg.id) === String(action.payload.id)
      )

      if (messageIndex !== -1) {
        const existing = state.messages[messageIndex]
        if (action.payload.message) {
          existing.message += action.payload.message
        }
        if (action.payload.streaming === false) {
          existing.streaming = false
        } else if (action.payload.streaming === true) {
          existing.streaming = true
        }
        if (action.payload.created_at !== undefined) {
          existing.created_at = action.payload.created_at
        }
        if (action.payload.llm_provider !== undefined) {
          existing.llm_provider = action.payload.llm_provider
        }
        if (action.payload.llm_model_name !== undefined) {
          existing.llm_model_name = action.payload.llm_model_name
        }
        if (action.payload.ragChunks !== undefined) {
          existing.ragChunks = action.payload.ragChunks
        }
      } else {
        state.messages.push(action.payload)
      }
    },
    setMessages: (state, action: PayloadAction<AIPlayerTestMessage[]>) => {
      state.messages = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
    clearMessages: (state) => {
      state.messages = []
    },
    setIsGenerating(state, action: PayloadAction<boolean>) {
      state.isGenerating = action.payload
    },
    setIsTestChatOpen(state, action: PayloadAction<boolean>) {
      state.isTestChatOpen = action.payload
    },
    setExistingTestChats(state, action: PayloadAction<ExistingAIPlayerTestChat[]>) {
      state.existingTestChats = action.payload
    },
    addOpenChatBox(
      state,
      action: PayloadAction<{
        chatData: ExistingAIPlayerTestChat
        isReadOnly: boolean
      }>
    ) {
      const { chatData, isReadOnly } = action.payload
      const existingChat = state.openChatBoxes.find((chat) => chat.id === chatData.id)
      if (existingChat) return

      const offsetX = state.openChatBoxes.length * (BOX_WIDTH + BOX_SPACING)
      state.openChatBoxes.push({
        id: chatData.id,
        chatData,
        isReadOnly,
        isTestChat: false,
        position: { right: BASE_RIGHT + offsetX, bottom: BASE_BOTTOM },
        dimensions: { width: BOX_WIDTH, height: 512 },
      })
    },
    addTestChatBox(state) {
      const existingTestChat = state.openChatBoxes.find((chat) => chat.isTestChat)
      if (existingTestChat) return

      const offsetX = state.openChatBoxes.length * (BOX_WIDTH + BOX_SPACING)
      state.openChatBoxes.push({
        id: AI_PLAYER_TEST_CHAT_ID,
        chatData: null,
        isReadOnly: false,
        isTestChat: true,
        position: { right: BASE_RIGHT + offsetX, bottom: BASE_BOTTOM },
        dimensions: { width: BOX_WIDTH, height: 512 },
      })
    },
    removeTestChatBox(state) {
      state.openChatBoxes = state.openChatBoxes.filter((chat) => !chat.isTestChat)
      state.openChatBoxes.forEach((chat, index) => {
        chat.position = {
          right: BASE_RIGHT + index * (BOX_WIDTH + BOX_SPACING),
          bottom: BASE_BOTTOM,
        }
      })
    },
    removeOpenChatBox(state, action: PayloadAction<number>) {
      state.openChatBoxes = state.openChatBoxes.filter(
        (chat) => chat.id !== action.payload
      )
      state.openChatBoxes.forEach((chat, index) => {
        chat.position = {
          right: BASE_RIGHT + index * (BOX_WIDTH + BOX_SPACING),
          bottom: BASE_BOTTOM,
        }
      })
    },
    updateChatBoxPosition(
      state,
      action: PayloadAction<{
        id: number
        position: { right: number; bottom: number }
      }>
    ) {
      const chatBox = state.openChatBoxes.find((chat) => chat.id === action.payload.id)
      if (chatBox) chatBox.position = action.payload.position
    },
    updateChatBoxDimensions(
      state,
      action: PayloadAction<{
        id: number
        dimensions: { width: number; height: number }
      }>
    ) {
      const chatBox = state.openChatBoxes.find((chat) => chat.id === action.payload.id)
      if (chatBox) chatBox.dimensions = action.payload.dimensions
    },
    clearAllOpenChatBoxes(state) {
      state.openChatBoxes = []
    },
  },
})

export const {
  setConnectionStatus: setAIPlayerTestChatConnectionStatus,
  addMessage: addAIPlayerTestChatMessage,
  updateMessage: updateAIPlayerTestChatMessage,
  setMessages: setAIPlayerTestChatMessages,
  setError: setAIPlayerTestChatError,
  clearMessages: clearAIPlayerTestChatMessages,
  setIsGenerating: setAIPlayerTestChatGenerating,
  setIsTestChatOpen: setIsAIPlayerTestChatOpen,
  setExistingTestChats: setExistingAIPlayerTestChats,
  addOpenChatBox: addAIPlayerOpenChatBox,
  addTestChatBox: addAIPlayerTestChatBox,
  removeTestChatBox: removeAIPlayerTestChatBox,
  removeOpenChatBox: removeAIPlayerOpenChatBox,
  updateChatBoxPosition: updateAIPlayerChatBoxPosition,
  updateChatBoxDimensions: updateAIPlayerChatBoxDimensions,
  clearAllOpenChatBoxes: clearAllAIPlayerOpenChatBoxes,
} = aiPlayerTestChatSlice.actions

export default aiPlayerTestChatSlice.reducer
